import { useState, useEffect, useRef } from "react";
import { ChevronDown, Globe } from "lucide-react";

const LANGUAGES = [
  { code: "bn", label: "bengali" },
  { code: "fr", label: "french" },
  { code: "gu", label: "gujarati" },
  { code: "hi", label: "hindi" },
  { code: "id", label: "bahasa indonesia" },
  { code: "jw", label: "bahasa jawa" },
  { code: "kn", label: "kannada" },
  { code: "mr", label: "marathi" },
  { code: "pt", label: "portuguese" },
  { code: "nso", label: "sepedi" },
  { code: "en", label: "english" },
  { code: "es", label: "spanish" },
  { code: "su", label: "bahasa sunda" },
  { code: "ta", label: "tamil" },
  { code: "te", label: "telugu" },
  { code: "xh", label: "isixhosa" },
  { code: "zu", label: "isizulu" },
  { code: "yo", label: "yoruba" },
  { code: "ig", label: "igbo" },
  { code: "ha", label: "hausa" },
].sort((a, b) => a.label.localeCompare(b.label));

export default function LanguageSwitcher() {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const getGoogTransDomainVariants = () => {
    const host = window.location.hostname;

    // localhost / IPs shouldn't get a Domain= attribute.
    const isLocalhost = host === "localhost";
    const isIp =
      /^(\d{1,3}\.){3}\d{1,3}$/.test(host) || host.includes(":"); // ipv4 / (very rough) ipv6

    const variants = new Set([""]); // "" => host-only cookie (no Domain=)

    if (!isLocalhost && !isIp) {
      variants.add(host);

      const parts = host.split(".").filter(Boolean);
      if (parts.length >= 2) {
        const base = parts.slice(-2).join(".");
        variants.add(base);
        variants.add(`.${base}`);
      }
    }

    return Array.from(variants);
  };

  const clearGoogTransCookie = () => {
    const variants = getGoogTransDomainVariants();
    variants.forEach((domain) => {
      document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;${domain ? ` domain=${domain};` : ""
        } SameSite=Lax`;
    });
  };

  const setGoogTransCookie = (value) => {
    const variants = getGoogTransDomainVariants();
    variants.forEach((domain) => {
      document.cookie = `googtrans=${value}; path=/; max-age=31536000;${domain ? ` domain=${domain};` : ""
        } SameSite=Lax`;
    });
  };

  const changeLanguage = (code) => {
    // Google Translate reads the `googtrans` cookie. If a cookie was previously set on a different
    // domain variant (e.g. `.example.com` vs `www.example.com`), simply writing a new cookie may
    // not override it. We clear all variants first, then set the chosen language.
    //
    // For English (default), we clear the cookie to stop translation.
    clearGoogTransCookie();
    if (code && code !== "en") setGoogTransCookie(`/en/${code}`);
    window.location.reload();
  };

  // Auto-detect OS/browser language on first visit (no saved preference)
  useEffect(() => {
    const existingCookie = document.cookie
      .split(';')
      .find(c => c.trim().startsWith('googtrans='));

    if (!existingCookie) {
      const osLang = (navigator.language || '').split('-')[0].toLowerCase();
      const supported = LANGUAGES.find(l => l.code === osLang);
      if (supported && osLang !== 'en') {
        setGoogTransCookie(`/en/${osLang}`);
        window.location.reload();
      }
    }
  }, []);

  // ✅ Outside click close (JS-safe)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={wrapperRef} className="relative notranslate overflow-visible">
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="
          notranslate
          text-white
          text-lg md:text-base
          font-medium
          flex items-center gap-2
          cursor-pointer
        "
      >
        <Globe size={18} />
        <ChevronDown
          size={18}
          className={`transition-transform duration-200 ${open ? "rotate-180" : ""
            }`}
        />
      </button>

      {open && (
        <>
          {/* Mobile overlay */}
          <div className="fixed inset-0 z-[9998] bg-black/40 md:hidden" />

          <ul
            className="
    notranslate
    z-[9999]

    fixed md:absolute
    bottom-24 md:bottom-auto
    left-1/2 -translate-x-1/2 md:left-auto md:translate-x-0
    md:right-0
    md:top-full
    mt-2

    w-[50%] max-w-sm md:w-48
    max-h-[50vh] md:max-h-[220px]
    overflow-y-auto

    bg-white text-black
    rounded-xl
    shadow-2xl
  "
          >

            {LANGUAGES.map((lang) => (
              <li
                key={lang.code}
                onClick={() => changeLanguage(lang.code)}
                className="
                  px-4 py-3
                  text-sm
                  cursor-pointer
                  hover:bg-gray-100
                  notranslate
                "
              >
                {lang.label}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
